import React, { useEffect, useState } from 'react';
import { Enrollment } from "@/types/classTypes";
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Trash2, Users } from 'lucide-react';
import { EnrollmentService } from '@/services/classServices/enrollmentService';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface StudentsTabProps {
  enrollments: Enrollment[];
  currentUserId: string;
  onEnrollmentUpdated?: () => Promise<void>;
  onManageWaitlist?: () => void;
}

interface FreeTrialStatus {
  student_id: string;
  status: string;
  trial_end_date: string;
}

const StudentsTab: React.FC<StudentsTabProps> = ({ enrollments, currentUserId, onEnrollmentUpdated, onManageWaitlist }) => {
  const [removingEnrollmentId, setRemovingEnrollmentId] = useState<string | null>(null);
  const [confirmRemoveEnrollment, setConfirmRemoveEnrollment] = useState<Enrollment | null>(null);
  const [showWaitlistPrompt, setShowWaitlistPrompt] = useState(false);

  // Split enrollments by status
  const activeEnrollments = enrollments.filter(e => e.status === 'active');
  const waitlistedEnrollments = enrollments.filter(e => 
    e.status === 'waitlisted'
  );

  const handleRemoveEnrollment = async (enrollment: Enrollment) => {
    if (!enrollment.id) return;
    
    setRemovingEnrollmentId(enrollment.id);
    try {
      await EnrollmentService.unenrollStudent(enrollment.id);
      toast.success(`${enrollment.student?.first_name} ${enrollment.student?.last_name} removed from class`);
      if (onEnrollmentUpdated) {
        await onEnrollmentUpdated();
      }
      
      // Check if there are waitlisted students and prompt to manage waitlist
      if (waitlistedEnrollments.length > 0) {
        setShowWaitlistPrompt(true);
      }
    } catch (error) {
      console.error('Error removing enrollment:', error);
      toast.error('Failed to remove student from class');
    } finally {
      setRemovingEnrollmentId(null);
      setConfirmRemoveEnrollment(null);
    }
  };

  const handleWaitlistPromptResponse = (shouldManage: boolean) => {
    setShowWaitlistPrompt(false);
    if (shouldManage && onManageWaitlist) {
      onManageWaitlist();
    }
  };

  return (
    <div className="space-y-4 pt-4">
      {enrollments.length === 0 ? (
        <div className="text-center py-6 text-muted-foreground">
          No students enrolled in this class yet.
        </div>
      ) : (
        <div>
          {activeEnrollments.length > 0 && (
            <div className="mb-6">
              <h3 className="font-medium mb-2">Enrolled Students ({activeEnrollments.length})</h3>
              <div className="space-y-2">
                {activeEnrollments.map(enrollment => (
                  <div key={enrollment.id} className="p-3 border rounded-md flex justify-between items-center">
                    <div>
                      {enrollment.student?.first_name} {enrollment.student?.last_name}
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setConfirmRemoveEnrollment(enrollment)}
                      disabled={removingEnrollmentId === enrollment.id}
                    >
                      {removingEnrollmentId === enrollment.id ? (
                        'Removing...'
                      ) : (
                        <>
                          <Trash2 className="h-4 w-4 mr-1" />
                          Remove
                        </>
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {waitlistedEnrollments.length > 0 && (
            <div>
              <h3 className="font-medium mb-2">Waitlisted Students ({waitlistedEnrollments.length})</h3>
              <div className="space-y-2">
                {waitlistedEnrollments.map(enrollment => (
                  <div key={enrollment.id} className="p-3 border rounded-md flex justify-between items-center bg-amber-50">
                    <div>
                      {enrollment.student?.first_name} {enrollment.student?.last_name}
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setConfirmRemoveEnrollment(enrollment)}
                      disabled={removingEnrollmentId === enrollment.id}
                    >
                      {removingEnrollmentId === enrollment.id ? (
                        'Removing...'
                      ) : (
                        <>
                          <Trash2 className="h-4 w-4 mr-1" />
                          Remove
                        </>
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Confirmation Dialog */}
      <AlertDialog open={!!confirmRemoveEnrollment} onOpenChange={() => setConfirmRemoveEnrollment(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Student from Class</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>{confirmRemoveEnrollment?.student?.first_name} {confirmRemoveEnrollment?.student?.last_name}</strong> from this class? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmRemoveEnrollment && handleRemoveEnrollment(confirmRemoveEnrollment)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove Student
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Waitlist Prompt Dialog */}
      <AlertDialog open={showWaitlistPrompt} onOpenChange={setShowWaitlistPrompt}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Manage Waitlist</AlertDialogTitle>
            <AlertDialogDescription>
              <div className="space-y-2">
                <p>A spot has opened up in this class.</p>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Users className="h-4 w-4" />
                  <span>{waitlistedEnrollments.length} student{waitlistedEnrollments.length !== 1 ? 's' : ''} on waitlist</span>
                </div>
                <p className="font-medium">Would you like to promote someone from the waitlist?</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => handleWaitlistPromptResponse(false)}>
              Not Now
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => handleWaitlistPromptResponse(true)}>
              Manage Waitlist
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default StudentsTab;
